// Live-chatfeed (Teamwork 50431665): lytter på serverens ChatwallHub (Intranet/Realtime/ChatwallHub.cs).
// Serveren sender kun { kind, itemType, postId }; selve opslaget hentes gennem de almindelige endpoints,
// så adgangsreglerne er de samme som i feedet.
//
// Kun WebSockets uden negotiate: så kræver forbindelsen ikke sticky sessions mellem serverne. Fejler den
// (fx en proxy uden WebSocket-support), opfører feedet sig som før — det opdaterer bare ikke sig selv.
import { HubConnectionBuilder, HubConnectionState, HttpTransportType, LogLevel } from "@microsoft/signalr";

const MAX_FAILED_STARTS = 5;

// En skjult fane lukker forbindelsen efter et minut: hver åben forbindelse er en aktiv request i IIS
// (på Windows 10/11 højst 10 for hele maskinen) og koster på serveren, og de fleste åbne intranetfaner
// ligger i baggrunden. Ventetiden undgår at forbindelsen slås til og fra, når man skifter fane et øjeblik.
// Når fanen vises igen, forbindes der på ny, og onReconnected henter det, der er gået tabt imens.
const HIDDEN_GRACE_MS = 60000;

/**
 * @param {{ groupId?: string|number, onMessage: (msg: {kind: string, itemType: string, postId: number}) => void, onReconnected?: () => void }} options
 * @returns {{ stop: () => Promise<void> }}
 */
export function connectChatwallLive({ groupId, onMessage, onReconnected }) {
  const query = groupId ? `?groupId=${encodeURIComponent(groupId)}` : "";
  const connection = new HubConnectionBuilder()
    .withUrl(`${process.env.VUE_APP_API_URL || ""}/api/intranet/chatwallhub${query}`, {
      transport: HttpTransportType.WebSockets,
      skipNegotiation: true,
    })
    .withAutomaticReconnect([0, 2000, 10000, 30000, 60000])
    .configureLogging(LogLevel.Warning)
    .build();

  connection.on("chatwall", (msg) => {
    if (msg && msg.postId) onMessage(msg);
  });
  connection.onreconnected(() => {
    if (onReconnected) onReconnected();
  });

  let stopped = false;
  // Lukket fordi fanen er skjult; startes igen når den vises.
  let paused = false;
  let pauseTimer = null;
  let stopping = null;
  let failedStarts = 0;

  async function start() {
    if (stopped || paused || connection.state !== HubConnectionState.Disconnected) return;
    try {
      await connection.start();
      failedStarts = 0;
    } catch (error) {
      // Serveren lukker også forbindelsen uden adgang (fx låst gruppe) — så giver vi op efter nogle forsøg.
      failedStarts++;
      if (failedStarts >= MAX_FAILED_STARTS) {
        console.warn("Chatwall-live: kunne ikke forbinde; feedet opdaterer ikke sig selv.", error);
        return;
      }
      setTimeout(start, Math.min(60000, 2000 * 2 ** failedStarts));
    }
  }

  // Når den automatiske genforbindelse har givet op (fx efter en genstart af sitet), prøv igen senere.
  connection.onclose(() => {
    if (!stopped && !paused) setTimeout(start, 30000);
  });

  function pause() {
    pauseTimer = null;
    if (stopped || paused) return;
    paused = true;
    stopping = connection.stop().catch(() => undefined).finally(() => {
      stopping = null;
    });
  }

  async function resume() {
    paused = false;
    failedStarts = 0;
    if (stopping) await stopping;
    await start();
    // Signaler kan være gået tabt mens fanen var skjult.
    if (connection.state === HubConnectionState.Connected && onReconnected) onReconnected();
  }

  function onVisibilityChange() {
    if (stopped) return;
    if (document.hidden) {
      if (!paused && !pauseTimer) pauseTimer = setTimeout(pause, HIDDEN_GRACE_MS);
      return;
    }
    if (pauseTimer) {
      // Tilbage inden for ventetiden: forbindelsen blev aldrig lukket.
      clearTimeout(pauseTimer);
      pauseTimer = null;
    }
    if (paused) resume();
  }

  document.addEventListener("visibilitychange", onVisibilityChange);
  start();
  if (document.hidden) onVisibilityChange();

  return {
    stop() {
      stopped = true;
      document.removeEventListener("visibilitychange", onVisibilityChange);
      if (pauseTimer) clearTimeout(pauseTimer);
      return connection.stop();
    },
  };
}
